import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import SelectionButton from 'modules/selection-button/index.js';
import PriceBoostBanner from 'modules/price-boost-banner/index.js';
import SelectionsContext from 'modules/selection-button/context/selections-context.js';
import PrebuiltBetSelectionsContext from 'modules/prebuilt-bets/prebuilt-bet/context/prebuilt-bet-selections-context.js';
import ShowAllSelectionsButton from 'modules/aggregated-market-pane/show-all-selections-button';
import PrebuiltBetUtils from 'modules/prebuilt-bets/prebuilt-bet/utils.js';
import MatchAccaSelectionItem from 'modules/match-acca/match-acca-selection-item/classic/index.js';
import BetslipModuleUtils from 'modules/betslip/utils.js';
import {
    useLimitedParts,
    usePrebuiltBetInfo,
} from 'modules/prebuilt-bets/prebuilt-bet/hooks/index.js';

import { useMemo, memo, useState } from 'react';

const { FlexPane, CardBackgroundImage, SportWatermarkImage, Button } =
    MojitoPresentation.Components;
const { PRE_BUILT_BET } = MojitoServices.Betslip.types.BETTING_CONTEXT;

// eslint-disable-next-line react/display-name
const PrebuiltBetClassic = memo(props => {
    const {
        mojitoTools: { config, l10n, style },
        event,
        market,
        boostedMarket,
        onEventClick,
        showMarketName,
        children,
        class: className,
        colorMode,
        icons,
    } = props;

    const details = PrebuiltBetUtils.resolveCardDetails(style, colorMode || config.mode);
    const { limitedParts, minimized, showAllButtonVisible, onToggleShowAllButton } =
        useLimitedParts(market?.preBuiltBetBuilderParts, details.partsThreshold);

    const selectionsContext = useMemo(
        () => new PrebuiltBetSelectionsContext(market, boostedMarket, event),
        [market, boostedMarket, event]
    );

    return (
        <FlexPane
            class={`ta-PrebuiltBet ${className}`.trim()}
            config={details.genericEventCard.container}
        >
            {(props.imgUrl || details.showBackgroundSportImage) && (
                <CardBackground {...props} config={details} />
            )}
            <Button
                class="ta-clickableArea"
                onClick={onEventClick}
                disabled={!details.showCardHeader || !details.cardClickable}
                config={details.genericEventCard.clickableArea}
            >
                {children}
                <FlexPane config={details.matchAccaSelectionsContainer}>
                    <MatchAccaSelections
                        icons={icons}
                        showLastChildSeparator={minimized && showAllButtonVisible}
                        parts={limitedParts}
                        showMarketName={showMarketName}
                        l10n={l10n}
                        config={details.matchAccaSelectionItem}
                    />
                </FlexPane>
            </Button>
            {showAllButtonVisible && (
                <ShowAllSelectionsButton
                    config={details.showAllSelectionsButton}
                    minimized={minimized}
                    onMinimizeChange={onToggleShowAllButton}
                />
            )}
            <SelectionsContext.Provider value={selectionsContext}>
                <CallToAction
                    config={details}
                    event={event}
                    market={market}
                    boostedMarket={boostedMarket}
                    l10n={l10n}
                />
            </SelectionsContext.Provider>
        </FlexPane>
    );
});

const CardBackground = props => {
    const { event, imgUrl, config } = props;
    const [failedImgUrl, setFailedImgUrl] = useState();

    if (imgUrl && imgUrl !== failedImgUrl) {
        return (
            <CardBackgroundImage
                src={imgUrl}
                config={config.cardBackgroundImage}
                onError={() => setFailedImgUrl(imgUrl)}
            />
        );
    }

    return <SportWatermarkImage sportId={event?.sportId} config={config.sportWatermark} />;
};

const MatchAccaSelections = ({
    parts,
    showMarketName,
    l10n,
    config,
    showLastChildSeparator,
    icons,
}) => {
    const showTeamKit = icons && Object.values(icons).some(icon => Boolean(icon));

    return (parts || []).map((part, i, row) => (
        <MatchAccaSelectionItem
            config={config}
            key={`${part.id}`}
            showSeparator={showLastChildSeparator || i + 1 !== row.length}
            selectionLabel={BetslipModuleUtils.resolveNameAndHandicapLabel(part, l10n)}
            marketName={showMarketName ? part.marketName : undefined}
            eventName={part.eventName}
            earlyPayout={part.earlyPayout}
            teamKitIcon={icons?.[part.id]}
            showTeamKit={showTeamKit}
        />
    ));
};

const CallToAction = ({ config, event, market, boostedMarket, l10n }) => {
    const betInfo = usePrebuiltBetInfo(market, boostedMarket, l10n);

    if (betInfo.basePrice) {
        return (
            <PriceBoostBanner
                config={config.priceBoostBanner}
                event={event}
                market={betInfo.market}
                basePrice={betInfo.basePrice}
                selection={betInfo.selection}
                bettingContext={PRE_BUILT_BET}
            />
        );
    }

    return (
        <FlexPane config={config.selectionContentContainer}>
            <SelectionButton
                config={config.selectionButton}
                selection={betInfo.selection}
                event={event}
                marketStatus={betInfo.market?.status}
                marketType={betInfo.market?.type}
                marketId={betInfo.market?.id}
                bettingContext={PRE_BUILT_BET}
            />
        </FlexPane>
    );
};

PrebuiltBetClassic.getStyle = (config, mode, merge) =>
    PrebuiltBetUtils.buildCardDetails(config, 'prebuiltBetDetailsDark', merge);

export default PrebuiltBetClassic;
